import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';
import { adminAPI } from '../services/api';

const ImageUpload = ({ 
  value, 
  onChange, 
  label = "Image",
  previewClassName = "w-full h-32 object-cover rounded-lg",
  required = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setImageLoaded(false);
    setUploading(true);

    try {
      const response = await adminAPI.uploadImage(file);
      if (response.data && response.data.url) {
        onChange(response.data.url);
      } else {
        setError('Upload failed - please try again');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed - please try again');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
    }
  };

  const removeImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange('');
    setError('');
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setError('');
  };

  const handleImageError = () => {
    setImageLoaded(false);
    // Don't set error for preview - just show placeholder
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={uploading}
        style={{ display: 'none' }}
      />

      {/* Upload Button - Always Visible */}
      <button
        type="button"
        onClick={triggerFileSelect}
        disabled={uploading}
        className={`w-full border-2 border-dashed rounded-lg p-4 transition-all ${
          uploading 
            ? 'border-gray-300 bg-gray-50 cursor-wait' 
            : 'border-gray-300 hover:border-[#007897] hover:bg-blue-50 cursor-pointer'
        }`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-[#007897] animate-spin" />
            <span className="text-sm text-gray-600">Uploading...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-gray-400" />
            <span className="text-sm text-gray-600">{value ? 'Click to change image' : 'Click to upload image'}</span>
            <span className="text-xs text-gray-400">All formats • No size limit</span>
          </div>
        )}
      </button>

      {/* Error Message */}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Image Preview */}
      {value && (
        <div className="relative border rounded-lg p-2 bg-gray-50">
          <div className="relative">
            {/* Always show the image container */}
            <div className={`relative overflow-hidden rounded-lg ${!imageLoaded ? 'bg-gray-200' : ''}`} style={{ minHeight: '120px' }}>
              <img 
                src={value} 
                alt="Uploaded preview" 
                className={`w-full h-32 object-cover rounded-lg transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              {/* Loading/Fallback overlay */}
              {!imageLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 rounded-lg">
                  <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-xs text-gray-500">Image saved</span>
                  <span className="text-xs text-gray-400">(Preview loading...)</span>
                </div>
              )}
            </div>
            
            {/* Remove Button */}
            <button
              type="button"
              onClick={removeImage}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-lg z-10"
              title="Remove image"
            >
              <X size={14} />
            </button>
          </div>
          
          {/* URL Display */}
          <p className="text-xs text-gray-400 mt-2 truncate" title={value}>
            ✓ Uploaded: {value.split('/').pop()}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
